package gr.tech.site.styles.landings

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import gr.tech.site.styles.Theme
import org.jetbrains.compose.web.css.*

// HERO LAYOUT

val LandingHeroStyle = CssStyle {
    base {
        Modifier
            .zIndex(1)
            .padding(topBottom = 6.cssRem, leftRight = 2.cssRem)
            .textAlign(TextAlign.Center)
            .color(Colors.White)
    }
}

// IMAGES

val LandingImageStyle = CssStyle {
    base {
        Modifier
            .width(350.px)
            .height(100.percent)
            .objectFit(ObjectFit.Contain)
            .borderRadius(16.px)
            .boxShadow(
                BoxShadow.of(
                    offsetX = 0.px,
                    offsetY = 8.px,
                    blurRadius = 24.px,
                    spreadRadius = 0.px,
                    color = Colors.Black
                )
            )
    }
}

// HERO TYPOGRAPHY

/** Small uppercase tagline above the hero headline */
val LandingHeroEyebrowStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.cssRem, 2.vw, 1.25.cssRem))
        .fontWeight(600)
        .color(Colors.White)
        .whiteSpace(WhiteSpace.NoWrap)
}

/** Primary H1 headline in the hero */
val LandingHeroHeadlineStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.5.cssRem, 4.5.vw, 3.cssRem))
        .fontWeight(700)
        .lineHeight(1.1)
        .color(Colors.White)
}

/** Lead paragraph beneath the hero headline */
val LandingHeroLeadStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.cssRem, 2.vw, 1.25.cssRem))
        .fontWeight(300)
        .color(Colors.White)
        .maxWidth(750.px)
}

// SECTION HEADINGS

/** H2 used on light-background sections */
val LandingSectionHeadingStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.4.cssRem, 3.5.vw, 2.25.cssRem))
        .fontWeight(700)
        .color(Theme.DarkGreen.rgb)
        .textAlign(TextAlign.Center)
}

/** H2 used on dark-background sections */
val LandingDarkSectionHeadingStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.4.cssRem, 3.5.vw, 2.25.cssRem))
        .fontWeight(700)
        .color(Colors.White)
        .textAlign(TextAlign.Center)
}

/** Muted subtitle/subheading directly beneath a section H2 */
val LandingSectionSubtitleStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .color(rgba(0, 0, 0, 0.5))
        .textAlign(TextAlign.Center)
}

// BODY TEXT

/** Standard body paragraph — inherits color from parent (works on both light and dark sections) */
val LandingBodyStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .lineHeight(1.7)
}

/** Muted body paragraph for secondary copy on light backgrounds */
val LandingMutedBodyStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .lineHeight(1.7)
        .color(rgba(0, 0, 0, 0.55))
}

val LandingMutedWhiteBodyStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .lineHeight(1.7)
        .color(rgba(255, 255, 255, 0.75))
}

/** Body paragraph for dark-background sections — white with slight transparency */
val LandingDarkBodyStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .lineHeight(1.7)
        .color(Colors.White)
        .opacity(90.percent)
        .maxWidth(700.px)
}

// STEP CARDS

val LandingStepCardNumberStyle = CssStyle.base {
    Modifier.Companion
        .backgroundColor(Theme.MediumGreen.rgb)
        .borderRadius(50.percent)
        .color(Colors.WhiteSmoke)
        .width(50.px)
        .height(50.px)
        .padding(1.cssRem)
}

val LandingStepCardTitleStyle = CssStyle.base {
    Modifier
        .fontSize(1.1.cssRem)
        .fontWeight(700)
        .color(Theme.DarkGreen.rgb)
}

val LandingStepCardBodyStyle = CssStyle.base {
    Modifier
        .fontSize(0.95.cssRem)
        .color(rgba(0, 0, 0, 0.55))
}

// REBATE CARDS

/**
 * Card used for each rebate category (LED, HVAC, etc.).
 * Background inverts to DarkGreen on hover — `color` inheritance turns child text white.
 * Child elements must NOT set explicit colors so hover inheritance works.
 */
val LandingRebateCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.White)
            .borderRadius(12.px)
            .border(2.px, LineStyle.Solid, Theme.MediumGreen.rgb)
            .padding(1.5.cssRem)
            .height(100.percent)
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier.Companion
            .backgroundColor(Theme.DarkGreen.rgb)
            .color(Colors.White)
            .translateY((-4).px)
            .boxShadow(
                BoxShadow.of(
                    offsetX = 0.px,
                    offsetY = 8.px,
                    blurRadius = 24.px,
                    spreadRadius = 0.px,
                    color = rgba(2, 67, 66, 0.25)
                )
            )
    }
}

/** H5 title inside a rebate card — no explicit color so it inherits on hover */
val LandingRebateCardTitleStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .fontWeight(700)
}

/** Body text inside a rebate card — no explicit color so it inherits on hover */
val LandingRebateCardBodyStyle = CssStyle.base {
    Modifier
        .fontSize(0.875.cssRem)
        .lineHeight(1.6)
}

// DARK SECTION ITEMS

/** Filled circle icon used in dark sections (Why Green Rebates checkmarks) */
val LandingCheckIconStyle = CssStyle.base {
    Modifier
        .width(44.px)
        .height(44.px)
        .minWidth(44.px)
        .borderRadius(50.percent)
        .backgroundColor(Theme.MediumGreen.rgb)
        .color(Colors.White)
        .fontSize(1.1.cssRem)
        .fontWeight(700)
}

/** H5 title for list items on dark backgrounds */
val LandingDarkItemTitleStyle = CssStyle.base {
    Modifier
        .fontSize(1.05.cssRem)
        .fontWeight(700)
        .color(Colors.White)
}

/** Body text for list items on dark backgrounds */
val LandingDarkItemBodyStyle = CssStyle.base {
    Modifier
        .fontSize(0.95.cssRem)
        .lineHeight(1.6)
        .color(Colors.White)
        .opacity(80.percent)
}

// WHO CAN BENEFIT

/** H2 used in split-layout sections (left-aligned on desktop) */
val LandingSubSectionHeadingStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.4.cssRem, 3.5.vw, 2.25.cssRem))
        .fontWeight(700)
        .color(Theme.DarkGreen.rgb)
}

/** Checkmark icon span in list items */
val LandingCheckmarkStyle = CssStyle.base {
    Modifier.Companion
        .color(Theme.MediumGreen.rgb)
        .fontWeight(700)
}

// TESTIMONIALS

/** Blockquote card for testimonials — left accent border + subtle shadow */
val LandingTestimonialCardStyle = CssStyle.base {
    Modifier
        .backgroundColor(Colors.White)
        .borderRadius(12.px)
        .borderLeft(4.px, LineStyle.Solid, Theme.MediumGreen.rgb)
        .padding(2.cssRem)
        .height(100.percent)
        .boxShadow(
            BoxShadow.of(
                offsetX = 0.px,
                offsetY = 4.px,
                blurRadius = 16.px,
                spreadRadius = 0.px,
                color = rgba(0, 0, 0, 0.08)
            )
        )
}

/** Large decorative opening quotation mark */
val LandingQuoteMarkStyle = CssStyle.base {
    Modifier.Companion
        .color(Theme.MediumGreen.rgb)
        .fontSize(3.cssRem)
        .lineHeight(1)
        .fontWeight(700)
}

/** Attribution line beneath a testimonial quote */
val LandingAttributionStyle = CssStyle.base {
    Modifier
        .fontSize(0.875.cssRem)
        .fontWeight(600)
        .color(Theme.MediumGreen.rgb)
}

// CTA SECTION

/** H2 headline in a full-width CTA banner section */
val LandingCtaHeadingStyle = CssStyle.base {
    Modifier
        .fontSize(clamp(1.5.cssRem, 4.vw, 2.5.cssRem))
        .fontWeight(700)
        .color(Colors.White)
}

/** Body paragraph in a CTA banner section */
val LandingCtaBodyStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .lineHeight(1.7)
        .color(Colors.White)
        .opacity(90.percent)
        .maxWidth(620.px)
}

// CTA BUTTONS

/** Primary CTA — used on hero and light sections (WhiteSmoke → MediumGreen on hover) */
val LandingCtaPrimaryStyle = CssStyle {
    base {
        Modifier.Companion
            .color(Theme.OverlayGreen.rgb)
            .backgroundColor(Colors.WhiteSmoke)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .fontSize(0.875.cssRem)
            .fontWeight(600)
            .boxShadow(BoxShadow.of(offsetX = 0.px, offsetY = 4.px, blurRadius = 14.px, spreadRadius = 0.px, color = rgba(0, 0, 0, 0.18)))
            .transition(Transition.all(250.ms))
    }
    Breakpoint.MD {
        Modifier
            .fontSize(1.25.cssRem)
    }
    hover {
        Modifier
            .color(Colors.WhiteSmoke)
            .backgroundColor(Theme.MediumGreen.rgb)
    }
}

/** Secondary CTA — MediumGreen fill, darkens to DarkGreen on hover */
val LandingCtaSecondaryStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .color(Colors.WhiteSmoke)
            .backgroundColor(Theme.MediumGreen.rgb)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .fontSize(1.25.cssRem)
            .fontWeight(600)
            .boxShadow(BoxShadow.of(offsetX = 0.px, offsetY = 4.px, blurRadius = 14.px, spreadRadius = 0.px, color = rgba(0, 0, 0, 0.18)))
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier
            .color(Colors.WhiteSmoke)
            .backgroundColor(Theme.DarkGreen.rgb)
    }
}

/** Secondary CTA — White fill, darkens to DarkGreen on hover */
val LandingCtaThirdStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .color(Theme.OverlayGreen.rgb)
            .backgroundColor(Colors.WhiteSmoke)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .fontSize(0.875.cssRem)
            .fontWeight(600)
            .boxShadow(BoxShadow.of(offsetX = 0.px, offsetY = 4.px, blurRadius = 14.px, spreadRadius = 0.px, color = rgba(0, 0, 0, 0.18)))
            .transition(Transition.all(250.ms))
    }
    Breakpoint.MD {
        Modifier
            .fontSize(1.25.cssRem)
    }
    hover {
        Modifier
            .color(Colors.WhiteSmoke)
            .backgroundColor(Theme.DarkGreen.rgb)
    }
}

/** Outline CTA — for dark section backgrounds. White border/text, fills white on hover */
val LandingCtaOutlineStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .color(Colors.White)
            .backgroundColor(Colors.Transparent)
            .border(2.px, LineStyle.Solid, Colors.White)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .fontSize(1.25.cssRem)
            .fontWeight(600)
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier.Companion
            .color(Theme.DarkGreen.rgb)
            .backgroundColor(Colors.White)
    }
}

val LandingCtaBlackOutlineStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.InlineBlock)
            .color(Colors.Black)
            .backgroundColor(Colors.Transparent)
            .border(2.px, LineStyle.Solid, Colors.Black)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .fontSize(1.25.cssRem)
            .fontWeight(600)
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier.Companion
            .color(Colors.White)
            .backgroundColor(Theme.DarkGreen.rgb)
    }
}

// kept for backward-compat use in other pages
val ButtonHoverOutline = LandingCtaOutlineStyle
